import { useState, useRef, useEffect } from 'react'
import ChatContent from './components/ChatContent'
import DisclaimerModal from './components/DisclaimerModal'
import UserTypeModal from './components/UserTypeModal'
import VetConfirmModal from './components/VetConfirmModal'
import LoginModal from './components/LoginModal'
import Sidebar from './components/Sidebar'

function App() {
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState([])
  const [current, setCurrent] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [userType, setUserType] = useState(null)
  const [showUserTypeModal, setShowUserTypeModal] = useState(false)
  const [questionCount, setQuestionCount] = useState(0)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [userData, setUserData] = useState(null)
  const [chatHistory, setChatHistory] = useState([])
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [showVetConfirmModal, setShowVetConfirmModal] = useState(false)
  const [isLoadingChats, setIsLoadingChats] = useState(false)
  const textareaRef = useRef(null)

  const fetchChatsFromServer = async () => {
    setIsLoadingChats(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}ai?chats_owners`, {
        credentials: 'include',
      })
      const result = await response.json()
      if (result.status === 'SUCCESS' && result.data) {
        setChatHistory(result.data)
      }
    } catch (error) {
      console.error('Error cargando charlas:', error)
    } finally {
      setIsLoadingChats(false)
    }
  }

  useEffect(() => {
    const savedUserType = localStorage.getItem('doctorvet_user_type')
    if (savedUserType) {
      setUserType(savedUserType)
    } else {
      setShowUserTypeModal(true)
    }

    const savedQuestionCount = localStorage.getItem('doctorvet_question_count')
    if (savedQuestionCount) {
      setQuestionCount(parseInt(savedQuestionCount, 10))
    }

    const loggedUser = localStorage.getItem('doctorvet_logged_user')
    if (loggedUser) {
      setIsLoggedIn(true)
      setUserData(JSON.parse(loggedUser))
      fetchChatsFromServer()
    }
  }, [])

  // Enfocar el textarea cuando se monta el componente
  useEffect(() => {
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
      }
    }, 200)
  }, [])

  // Función para actualizar la URL con el ID de conversación
  const updateUrlWithConversationId = (id) => {
    const url = new URL(window.location)
    url.searchParams.set('id', id)
    window.history.pushState({}, '', url)
  }

  const handleSendMessage = async (assistantFirstMessage = null) => {
    if (message.trim() && !isLoading) {
      // Verificar si NO_VET necesita login (más de 3 mensajes)
      if (userType === 'NO_VET' && questionCount >= 3 && !isLoggedIn) {
        setShowLoginModal(true)
        return
      }

      const userMessage = message
      setMessages(prev => [...prev, { type: 'user', content: userMessage }])
      setIsLoading(true)
      setMessage('')

      // Incrementar y guardar el conteo de preguntas
      const newCount = questionCount + 1
      setQuestionCount(newCount)
      localStorage.setItem('doctorvet_question_count', newCount.toString())

      const isNewConversation = !conversationId

      try {
        // Limpiar respuesta parcial
        setCurrent('')

        const response = await fetch(`${import.meta.env.VITE_API_URL}ai?chat_completions_assistant`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            message: userMessage,
            id: conversationId,
            user: userType
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        let newChatId = null
        const conversationIdFromHeader = response.headers.get('X-Chat-ID')
        if (conversationIdFromHeader) {
          newChatId = conversationIdFromHeader
          setConversationId(conversationIdFromHeader)
          updateUrlWithConversationId(conversationIdFromHeader)
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let fullResponse = "";
        let buffer = "";
        let chatTitle = null;

        const processChunk = (line) => {
          if (line.trim() === "") return
          try {
            const chunkData = JSON.parse(line);
            if (chunkData.choices?.[0]?.delta?.content) {
              const content = chunkData.choices[0].delta.content;
              fullResponse += content;
              setCurrent(prev => prev + content);
            }
            if (chunkData.choices?.[0]?.finish_reason === 'stop' &&
                chunkData.choices?.[0]?.delta?.title) {
              chatTitle = chunkData.choices[0].delta.title;
            }
          } catch (error) {
            console.error('Error parseando chunk JSON:', error, 'Línea:', line);
          }
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || "";

          for (const line of lines) {
            processChunk(line)
          }
        }

        // Procesar cualquier dato restante en el buffer
        if (buffer.trim()) {
          processChunk(buffer)
        }

        // Cuando termina, lo pasamos a mensajes definitivos
        setMessages(prev => [...prev, { type: 'assistant', content: fullResponse }]);
        setCurrent('');

        // Agregar el nuevo chat al historial del sidebar sin llamada extra al servidor
        if (isNewConversation && isLoggedIn && newChatId) {
          setChatHistory(prev => [{
            id: newChatId,
            title: chatTitle || 'Nueva charla',
            created_at: new Date().toISOString()
          }, ...prev])
        }
        
      } catch (error) {
        setMessages(prev => {
          const newMessages = [...prev]
          const lastMessage = newMessages[newMessages.length - 1]
          if (lastMessage && lastMessage.type === 'assistant') {
            lastMessage.content = 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.'
          }
          return newMessages
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleUserTypeSelect = (type) => {
    setUserType(type)
    setShowUserTypeModal(false)
  }

  const handleUserTypeChange = (type) => {
    if (type === userType) return
    if (type === 'VET') {
      setShowVetConfirmModal(true)
      return
    }
    setUserType(type)
    localStorage.setItem('doctorvet_user_type', type)
    handleNewChat()
  }

  const handleVetConfirm = () => {
    setShowVetConfirmModal(false)
    setUserType('VET')
    localStorage.setItem('doctorvet_user_type', 'VET')
    handleNewChat()
  }

  const handleLogin = (user) => {
    setIsLoggedIn(true)
    setUserData(user)
    setShowLoginModal(false)
    fetchChatsFromServer()
  }

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      await fetch(`${import.meta.env.VITE_API_URL}owners?logout_account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
    } catch {
      // Cerrar sesión localmente aunque falle la API
    } finally {
      localStorage.removeItem('doctorvet_logged_user')
      setIsLoggedIn(false)
      setUserData(null)
      setIsLoggingOut(false)
    }
  }

  // Nueva charla
  const handleNewChat = () => {
    setMessages([])
    setCurrent('')
    setConversationId(null)
    setMessage('')
    // Limpiar URL
    const url = new URL(window.location)
    url.searchParams.delete('id')
    window.history.pushState({}, '', url)
    // Enfocar textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
      }
    }, 100)
    // Cerrar sidebar en móvil
    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }
  }

  const handleSelectChat = async (chatId) => {
    if (chatId === conversationId) return

    setConversationId(chatId)
    setMessages([])
    setCurrent('')
    setIsLoading(true)

    const url = new URL(window.location)
    url.searchParams.set('id', chatId)
    window.history.pushState({}, '', url)

    if (window.innerWidth < 768) {
      setSidebarOpen(false)
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}ai?chat_id_owners=${chatId}`, {
        credentials: 'include',
      })
      const result = await response.json()
      if (result.status === 'SUCCESS' && result.data?.messages) {
        setMessages(result.data.messages.map(msg => ({
          type: msg.role,
          content: msg.content
        })))
      }
    } catch (error) {
      console.error('Error cargando mensajes:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {/* Modal de selección de tipo de usuario (primera vez) */}
      {showUserTypeModal && (
        <UserTypeModal onSelect={handleUserTypeSelect} />
      )}

      {/* Modal de Términos y Condiciones (se abre al hacer clic en el link) */}
      {showDisclaimer && (
        <DisclaimerModal onClose={() => setShowDisclaimer(false)} />
      )}

      {/* Modal de confirmación modo Veterinario */}
      {showVetConfirmModal && (
        <VetConfirmModal 
          onConfirm={handleVetConfirm} 
          onCancel={() => setShowVetConfirmModal(false)} 
        />
      )}

      {/* Modal de Login (para NO_VET con más de 3 mensajes) */}
      {showLoginModal && (
        <LoginModal onLogin={handleLogin} onClose={() => setShowLoginModal(false)} />
      )}

      {/* Layout con Sidebar cuando el usuario está logueado */}
      <div className="flex h-dvh min-h-0">
        {/* Sidebar - solo visible cuando está logueado */}
        {isLoggedIn && (
          <Sidebar 
            user={userData}
            chatHistory={chatHistory}
            currentChatId={conversationId}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onLogout={handleLogout}
            isLoggingOut={isLoggingOut}
            isLoadingChats={isLoadingChats}
            isOpen={sidebarOpen}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
          />
        )}
        
        {/* Chat Content - con margen izquierdo cuando hay sidebar */}
        <div className={`flex min-h-0 flex-1 flex-col transition-all duration-300 ${isLoggedIn ? 'md:ml-72' : ''}`}>
          <ChatContent 
            messages={messages}
            current={current}
            message={message}
            setMessage={setMessage}
            handleSendMessage={handleSendMessage}
            handleKeyPress={handleKeyPress}
            textareaRef={textareaRef}
            isLoading={isLoading}
            isLoggedIn={isLoggedIn}
            userType={userType}
            onUserTypeChange={handleUserTypeChange}
            onLoginClick={() => setShowLoginModal(true)}
            onOpenTerms={() => setShowDisclaimer(true)}
          />
        </div>
      </div>
    </>
  )
}

export default App

